import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";

export type PageAlertType = "success" | "error";

export interface PageAlertMessage {
  id: string;
  type?: PageAlertType;
  title: string;
  description?: string;
  linkTo?: string;
  linkLabel?: string;
  duration?: number;
}

type PageAlertInput = Omit<PageAlertMessage, "id">;

interface PageAlertContextData {
  alert: PageAlertMessage | null;
  showPageAlert(message: PageAlertInput): void;
  dismissPageAlert(id?: string): void;
  registerRenderer(priority: number, onActiveChange: (active: boolean) => void): () => void;
  hasActiveRenderer(): boolean;
}

const DEFAULT_AUTO_DISMISS_MS = 6000;

interface RegisteredRenderer {
  id: number;
  priority: number;
  onActiveChange: (active: boolean) => void;
}

const PageAlertContext = createContext<PageAlertContextData>({} as PageAlertContextData);

let showPageAlertBridge: ((message: PageAlertInput) => void) | null = null;
let dismissPageAlertBridge: ((id?: string) => void) | null = null;
let hasActiveRendererBridge: (() => boolean) | null = null;
let isProgressFeedbackActiveBridge: (() => boolean) | null = null;
let nextRendererId = 0;
let privatePageAlertScope = false;
let pendingPageAlert: PageAlertInput | null = null;

export function setProgressFeedbackActiveBridge(active: () => boolean) {
  isProgressFeedbackActiveBridge = active;
}

export function dismissActivePageAlert() {
  dismissPageAlertBridge?.();
}

export function setPrivatePageAlertScope(active: boolean) {
  privatePageAlertScope = active;
  if (!active) {
    pendingPageAlert = null;
  }
}

function flushPendingPageAlert() {
  if (!pendingPageAlert || !hasActiveRendererBridge?.()) {
    return;
  }

  if (isProgressFeedbackActiveBridge?.()) {
    return;
  }

  const alert = pendingPageAlert;
  pendingPageAlert = null;
  showPageAlertBridge?.(alert);
}

export function routeToastToPageAlert(message: PageAlertInput): boolean {
  if (isProgressFeedbackActiveBridge?.()) {
    return true;
  }

  const alert = { type: "success" as const, ...message };

  if (hasActiveRendererBridge?.()) {
    pendingPageAlert = null;
    showPageAlertBridge?.(alert);
    return true;
  }

  if (privatePageAlertScope) {
    pendingPageAlert = alert;
    return true;
  }

  return false;
}

export function isProgressFeedbackActive(): boolean {
  return isProgressFeedbackActiveBridge?.() ?? false;
}

const PageAlertProvider: React.FC = ({ children }) => {
  const [alert, setAlert] = useState<PageAlertMessage | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renderersRef = useRef<RegisteredRenderer[]>([]);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const syncActiveRenderer = useCallback(() => {
    const renderers = renderersRef.current;
    if (renderers.length === 0) {
      return;
    }

    const maxPriority = Math.max(...renderers.map((renderer) => renderer.priority));
    const candidates = renderers.filter((renderer) => renderer.priority === maxPriority);
    const activeRenderer = candidates[candidates.length - 1];

    renderers.forEach((renderer) => {
      renderer.onActiveChange(renderer.id === activeRenderer?.id);
    });
  }, []);

  const dismissPageAlert = useCallback(
    (id?: string) => {
      clearDismissTimer();
      setAlert((current) => {
        if (!current) return null;
        if (id && current.id !== id) return current;
        return null;
      });
    },
    [clearDismissTimer]
  );

  const showPageAlert = useCallback(
    ({ type = "success", title, description, linkTo, linkLabel, duration }: PageAlertInput) => {
      if (isProgressFeedbackActiveBridge?.()) {
        return;
      }

      clearDismissTimer();

      const nextAlert: PageAlertMessage = {
        id: uuidv4(),
        type,
        title,
        description,
        linkTo,
        linkLabel,
        duration,
      };

      setAlert(nextAlert);

      const timeout = duration ?? DEFAULT_AUTO_DISMISS_MS;
      if (timeout > 0) {
        dismissTimerRef.current = setTimeout(() => {
          dismissPageAlert(nextAlert.id);
        }, timeout);
      }
    },
    [clearDismissTimer, dismissPageAlert]
  );

  const registerRenderer = useCallback(
    (priority: number, onActiveChange: (active: boolean) => void) => {
      const renderer: RegisteredRenderer = {
        id: ++nextRendererId,
        priority,
        onActiveChange,
      };

      renderersRef.current = [...renderersRef.current, renderer];
      syncActiveRenderer();
      flushPendingPageAlert();

      return () => {
        renderer.onActiveChange(false);
        renderersRef.current = renderersRef.current.filter((item) => item.id !== renderer.id);
        syncActiveRenderer();
      };
    },
    [syncActiveRenderer]
  );

  const hasActiveRenderer = useCallback(
    () => renderersRef.current.length > 0,
    []
  );

  useEffect(() => {
    showPageAlertBridge = showPageAlert;
    dismissPageAlertBridge = dismissPageAlert;
    hasActiveRendererBridge = hasActiveRenderer;

    return () => {
      showPageAlertBridge = null;
      dismissPageAlertBridge = null;
      hasActiveRendererBridge = null;
      clearDismissTimer();
    };
  }, [clearDismissTimer, dismissPageAlert, hasActiveRenderer, showPageAlert]);

  const contextValue = useMemo(
    () => ({
      alert,
      showPageAlert,
      dismissPageAlert,
      registerRenderer,
      hasActiveRenderer,
    }),
    [alert, dismissPageAlert, hasActiveRenderer, registerRenderer, showPageAlert]
  );

  return (
    <PageAlertContext.Provider value={contextValue}>
      {children}
    </PageAlertContext.Provider>
  );
};

function usePageAlert() {
  const context = useContext(PageAlertContext);
  if (!context) {
    throw new Error("usePageAlert must be used within a PageAlertProvider");
  }
  return context;
}

export { PageAlertProvider, usePageAlert };
